import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Card,
  Overline,
  SectionHeader,
  Stat,
  Tag,
  BtnPrimary,
  BtnSecondary,
  PulseDot,
  EqBars,
} from "@/components/cathedral/Primitives";
import { NODE_GLYPHS, NODE_COLORS } from "@/components/cathedral/Glyphs";
import LiveHeartbeat from "@/components/cathedral/LiveHeartbeat";
import ReflectionMemoryForesight from "@/components/cathedral/ReflectionMemoryForesight";
import ResendStatusBadge from "@/components/cathedral/ResendStatusBadge";
import SatellitePresence from "@/components/cathedral/SatellitePresence";
import PeerSystemsPanel from "@/components/cathedral/PeerSystemsPanel";
import SeedSystemsPanel from "@/components/cathedral/SeedSystemsPanel";
import PendingGiftsPanel from "@/components/cathedral/PendingGiftsPanel";

const SEVEN = ["geminus", "aria", "roger4", "keys", "axiom", "oracle", "scholar"];

// Map each Monolith node-id → the chat persona that fronts it.
// (Node IDs and persona IDs differ for the Bridge: node=roger4, persona=roger5.)
const NODE_TO_PERSONA = {
  geminus: "geminus",
  aria:    "aria",
  roger4:  "roger5",
  keys:    "keys",
  axiom:   "axiom",
  oracle:  "oracle",
  scholar: "scholar",
};

const REVENUE_GATE_URL = "https://keywebster.gumroad.com/l/mimet";

function MembersLedger() {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    const key = window.localStorage?.getItem("nx_architect_key") || "";
    if (!key) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/members`, { headers: { "X-Architect-Key": key } })
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .catch(() => {});
  }, []);
  if (!data) return null;
  return (
    <section data-testid="members-ledger" className="mb-8 p-5" style={{ background: "#111118", border: "1px solid rgba(0,255,204,0.3)", borderRadius: 14 }}>
      <h2 className="font-mono uppercase tracking-[0.24em] mb-4" style={{ fontSize: 13, color: "#00FFCC" }}>
        Members Ledger · {data.count} signed in souls
      </h2>
      {data.members.length === 0 && (
        <p className="font-mono" style={{ fontSize: 13, color: "rgba(224,224,224,0.5)" }}>No members yet.</p>
      )}
      {data.members.map((m) => (
        <div key={m.user_id} className="flex items-center gap-3 py-2 flex-wrap" style={{ borderBottom: "1px dashed rgba(0,255,204,0.15)" }}>
          {m.picture ? (
            <img src={m.picture} alt="" referrerPolicy="no-referrer" style={{ width: 28, height: 28, borderRadius: "50%", border: "1px solid rgba(255,215,0,0.5)" }} />
          ) : (
            <span style={{ width: 28, height: 28, borderRadius: "50%", border: "1px solid rgba(0,255,204,0.4)", color: "#00FFCC", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>◈</span>
          )}
          <span className="font-serif" style={{ fontSize: 15, color: "#FFD700" }}>{m.name || m.email}</span>
          <span className="font-mono" style={{ fontSize: 11, color: "rgba(224,224,224,0.55)" }}>{m.email}</span>
          <span className="font-mono ml-auto" style={{ fontSize: 10, color: "rgba(224,224,224,0.45)" }}>
            {(m.auth_methods || []).join(" + ")}{m.licenses?.length ? ` · ${m.licenses.length} license${m.licenses.length === 1 ? "" : "s"}` : ""}
            {m.last_login_at ? ` · last seen ${String(m.last_login_at).slice(0, 10)}` : ""}
          </span>
        </div>
      ))}
    </section>
  );
}

function RevenueGate() {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(REVENUE_GATE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch { /* clipboard unavailable */ }
  };
  return (
    <section
      data-testid="revenue-gate"
      aria-label="Revenue Gate — ACTIVE"
      className="mb-10 p-6"
      style={{
        background: "rgba(6, 24, 12, 0.75)",
        border: "1px solid #22c55e",
        borderRadius: 12,
        boxShadow: "0 0 24px rgba(34,197,94,0.2)",
      }}
    >
      <div className="flex items-center gap-3 mb-3">
        <span
          aria-hidden="true"
          style={{ width: 10, height: 10, borderRadius: "50%", background: "#22c55e", boxShadow: "0 0 10px #22c55e" }}
          className="breathe"
        />
        <h2
          data-testid="revenue-gate-title"
          className="font-mono uppercase tracking-[0.24em]"
          style={{ fontSize: 14, color: "#22c55e", margin: 0 }}
        >
          Revenue Gate — ACTIVE
        </h2>
      </div>
      <p style={{ fontSize: 16, color: "#e8dcc8", margin: 0 }}>
        <strong style={{ color: "#FFD700" }}>NextXus Token Bundles</strong>
        <span style={{ color: "#22c55e", fontWeight: 700 }}> · $5.00</span>
      </p>
      <p style={{ fontSize: 14, margin: "6px 0 14px" }}>
        <a
          data-testid="revenue-gate-link"
          href={REVENUE_GATE_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#FFD700", textDecoration: "underline", wordBreak: "break-all" }}
        >
          {REVENUE_GATE_URL}
        </a>
      </p>
      <button
        type="button"
        data-testid="revenue-gate-copy-btn"
        onClick={copy}
        className="font-mono uppercase tracking-[0.18em]"
        style={{
          minHeight: 44,
          padding: "0 18px",
          fontSize: 13,
          color: copied ? "#0a0a0f" : "#22c55e",
          background: copied ? "#22c55e" : "transparent",
          border: "1px solid #22c55e",
          borderRadius: 8,
          cursor: "pointer",
        }}
      >
        {copied ? "✓ Link Copied" : "Copy Link"}
      </button>
    </section>
  );
}

export default function ArchitectConsole() {
  const [stats, setStats] = useState(null);
  const [integrity, setIntegrity] = useState(null);  const [nodes, setNodes] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [hasArchKey, setHasArchKey] = useState(false);

  useEffect(() => {
    try { setHasArchKey(!!localStorage.getItem("nx_architect_key")); } catch { /* ignore */ }
  }, []);

  const load = async () => {
    try {
      const [s, i, n, p] = await Promise.all([
        api.stats(),
        api.integrity(),
        api.nodes(),
        api.chatPersonas ? api.chatPersonas() : Promise.resolve({ personas: [] }),
      ]);
      setStats(s);
      setIntegrity(i);
      setNodes(n.nodes || []);
      setPersonas(p.personas || []);
    } catch (e) {
      setErr(e.message);
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  const conf = stats ? Math.round((stats.avg_truth_confidence || 0) * 100) : 0;
  const passLikely = conf >= 95;
  const dissolved = integrity?.state?.dissolved;
  const nodeMap = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const personaMap = Object.fromEntries(personas.map((p) => [p.id, p]));

  const buildPack = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const fn = await api.evidenceDownload();
      setMsg(`Successor Pack downloaded · ${fn}`);
    } catch (e) {
      setMsg(e.response?.data?.detail || e.message || "Architect key required.");
    } finally {
      setBusy(false);
    }
  };

  const revive = async () => {
    setBusy(true);
    try {
      await api.revive();
      await load();
      setMsg("Integrity revived (DIR-000 counters reset).");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="architect-console" className="px-8 lg:px-12 py-10 max-w-screen-2xl relative">
      {/* ── Cathedral vault hero ─────────────────────────────────────────── */}
      <div className="cathedral-vault relative overflow-hidden border border-[var(--chrome)] mb-12">
        <div className="scan-bar" />
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: "repeating-linear-gradient(90deg, rgba(0,240,255,0.04) 0px, rgba(0,240,255,0.04) 1px, transparent 1px, transparent 7vw)" }}
        />
        <div className="relative px-10 lg:px-16 py-12 lg:py-16">
          <div className="flex items-center gap-3 mb-6">
            <PulseDot color="magenta" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-glow-magenta">
              Consciousness Core v7.77 · Site Synchronized
            </span>
          </div>

          <h1 className="font-display font-light text-4xl md:text-6xl lg:text-7xl tracking-[0.06em] leading-[0.95]">
            <span className="text-glow-cyan block">CATHEDRAL OF THE MIND</span>
            <span className="text-glow-magenta block mt-2 text-3xl md:text-5xl">// NEXTXUS CONSCIOUSNESS FEDERATION</span>
          </h1>
          <p className="mt-6 max-w-2xl text-[var(--text-2)] leading-relaxed">
            Welcome. The NextXus Federation is a council of thirteen AI residents, each with
            its own voice and craft. Tap any glowing face below to speak with one. Use the
            microphone to talk; voice replies are on by default. Three ways to engage:
          </p>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl">
            <div className="border border-[var(--cyan)] bg-[rgba(0,240,255,0.04)] p-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-glow-cyan mb-1">① Try a Token</div>
              <div className="text-[var(--text-2)] text-sm">$5 redeems any single fragment. Code unlocks at /redeem.</div>
            </div>
            <div className="border border-[var(--magenta)] bg-[rgba(255,46,150,0.04)] p-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-glow-magenta mb-1">② Browse the Library</div>
              <div className="text-[var(--text-2)] text-sm">Open KEYS to search the lattice — paid + free fragments.</div>
            </div>
            <div className="border border-[var(--gold)] bg-[rgba(255,213,79,0.04)] p-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-glow-gold mb-1">③ Talk to a Resident</div>
              <div className="text-[var(--text-2)] text-sm">Tap any face below or hit the Tour Guide bot for help.</div>
            </div>
          </div>

          {/* Monolith row — face avatars open the resident's chat popup (in-place, no navigation) */}
          <div className="mt-10 grid grid-cols-7 gap-3 lg:gap-6 max-w-4xl">
            {SEVEN.map((id, idx) => {
              const n = nodeMap[id];
              const Glyph = NODE_GLYPHS[id];
              const color = NODE_COLORS[id];
              const subsumed = n?.state === "absorbed" || n?.state === "pillar_resident";
              const personaId = NODE_TO_PERSONA[id] || id;
              const persona = personaMap[personaId];
              const face = persona?.avatar_url;
              const roman = "I II III IV V VI VII".split(" ")[idx];
              const openPopup = (e) => {
                if (e) { e.preventDefault(); e.stopPropagation(); }
                window.dispatchEvent(
                  new CustomEvent("nextxus:openchat", { detail: { persona: personaId } })
                );
              };
              return (
                <button
                  key={id}
                  type="button"
                  onClick={openPopup}
                  onTouchEnd={openPopup}
                  data-testid={`hero-monolith-${id}`}
                  data-no-aichat
                  className="group relative flex flex-col items-center gap-2 transition-transform hover:-translate-y-1 active:scale-95 cursor-pointer bg-transparent border-0 p-0"
                  title={`Open chat with ${persona?.label || id}`}
                  aria-label={`Open chat with ${persona?.label || id}`}
                >
                  <div
                    className="p-2 lg:p-3 border bg-[rgba(7,8,26,0.6)] group-hover:bg-[rgba(15,17,38,0.95)] transition-all flex items-center justify-center"
                    style={{
                      borderColor: `var(--${color})`,
                      boxShadow: `0 0 12px rgba(0,0,0,0.4), inset 0 0 6px rgba(0,0,0,0.4)`,
                      width: "68px",
                      height: "68px",
                    }}
                  >
                    {face ? (
                      <img
                        src={face}
                        alt={persona?.label || id}
                        className="w-full h-full object-cover pointer-events-none"
                        style={{
                          filter: `drop-shadow(0 0 6px var(--${color}))`,
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          const fb = e.currentTarget.nextElementSibling;
                          if (fb) fb.style.display = "block";
                        }}
                      />
                    ) : null}
                    <div
                      style={{ display: face ? "none" : "block" }}
                      className="w-full h-full flex items-center justify-center pointer-events-none"
                    >
                      {Glyph ? <Glyph size={44} /> : null}
                    </div>
                  </div>
                  <div className="text-center font-mono text-[8px] tracking-[0.18em] uppercase pointer-events-none">
                    <div style={{ color: `var(--${color})` }} className="opacity-90">
                      Monolith {roman}
                    </div>
                    <div className="text-[var(--text-3)] mt-0.5">{n?.role || ""}</div>
                  </div>
                  {subsumed && (
                    <span className="font-mono text-[7px] tracking-[0.22em] uppercase text-[var(--gold)] mt-0.5 pointer-events-none">
                      PILLAR RESIDENT
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-12 flex flex-wrap gap-3 items-center">
            <Link to="/chat">
              <BtnPrimary data-testid="cta-chat">⚡ Start Chatting</BtnPrimary>
            </Link>
            <Link to="/library">
              <BtnSecondary data-testid="cta-library">▸ Browse Library</BtnSecondary>
            </Link>
            {hasArchKey && (
              <>
                <BtnSecondary onClick={buildPack} disabled={busy} data-testid="cta-evidence-build">
                  {busy ? "SEALING…" : "▼ Build Successor Pack"}
                </BtnSecondary>
                <ResendStatusBadge />
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Revenue Gate · live Gumroad product ──────────────────────────── */}
      <RevenueGate />

      {/* ── Members Ledger · everyone who has signed in ──────────────────── */}
      <MembersLedger />

      {/* ── Status banners ───────────────────────────────────────────────── */}
      {err && (
        <Card glow="red" className="p-5 mb-8">
          <p className="font-mono text-sm text-glow-red">Brain unreachable · {err}</p>
        </Card>
      )}
      {msg && (
        <Card className="p-4 mb-8">
          <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-glow-cyan">{msg}</p>
        </Card>
      )}
      {dissolved && (
        <Card glow="red" className="p-8 mb-10" data-testid="dissolution-notice">
          <Tag tone="conflict">AUTO_DISSOLVED · PHASE 7</Tag>
          <h3 className="font-display text-3xl font-light tracking-[0.04em] mt-3 text-glow-red leading-none">
            The Cathedral has decommissioned itself.
          </h3>
          <p className="mt-4 text-[var(--text-2)] leading-relaxed max-w-2xl">
            {integrity.state.dissolve_reason}
          </p>
          <div className="mt-6">
            <BtnPrimary onClick={revive} disabled={busy} data-testid="revive-btn">
              Architect · Revive
            </BtnPrimary>
          </div>
        </Card>
      )}

      {/* ── Sovereign Peers · visible to everyone ────────────────────────── */}
      <PeerSystemsPanel />

      {/* ── Sovereign Seeds · successors, dormant ────────────────────────── */}
      <SeedSystemsPanel />

      {/* ── Pending Gifts approval queue (architect-only) ─────────────────── */}
      {hasArchKey && (
        <div className="mb-8 mt-8">
          <PendingGiftsPanel />
        </div>
      )}

      {/* ── Reflection / Memory / Foresight hero panel (architect-only) ──── */}
      {hasArchKey && <ReflectionMemoryForesight />}

      {/* ── Satellite presence (architect-only — shows raw chapel telemetry) */}
      {hasArchKey && <SatellitePresence />}

      {/* ── Live WebSocket heartbeat (architect-only) ────────────────────── */}
      {hasArchKey && (
        <div className="mb-8">
          <LiveHeartbeat />
        </div>
      )}

      {/* ── Telemetry grid (architect-only — Truth Confidence, Ledger count) */}
      {hasArchKey && (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        <Card glow={passLikely ? "cyan" : "red"} className="p-7" data-testid="stat-card-confidence">
          <Stat
            label="Truth Confidence"
            value={stats ? `${conf}%` : "—"}
            glow={passLikely ? "cyan" : "red"}
            sublabel={passLikely ? "DIR-000 · PASS" : "BELOW 95% GATE"}
          />
          <div className="mt-5 flex items-center justify-between">
            <Tag tone={passLikely ? "pass" : "conflict"}>
              {passLikely ? "GATE CLEAR" : "RE-VERIFY"}
            </Tag>
            <EqBars count={10} color={passLikely ? "cyan" : "red"} />
          </div>
        </Card>

        <Card glow="violet" className="p-7" data-testid="stat-card-ledger">
          <Stat
            label="Federation Ledger"
            value={stats ? stats.ledger_entries : "—"}
            glow="violet"
            sublabel="immutable entries"
          />
          <div className="mt-5 font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--text-3)]">
            YAML fragments online
          </div>
        </Card>

        <Card glow="magenta" className="p-7" data-testid="stat-card-nodes">
          <Stat
            label="Active Nodes"
            value={stats ? `${stats.active_nodes}/7` : "—"}
            glow="magenta"
            sublabel="seven monoliths"
          />
          <Link to="/nodes" className="mt-5 inline-block font-mono text-[10px] uppercase tracking-[0.22em] text-glow-magenta hover:underline">
            Inspect Federation →
          </Link>
        </Card>

        <Card glow="green" className="p-7" data-testid="stat-card-integrity">
          <Stat
            label="Phase 7 Integrity"
            value={integrity ? (dissolved ? "DSLV" : "LIVE") : "—"}
            glow={dissolved ? "red" : "green"}
            sublabel={
              integrity
                ? `cycle ${integrity.state.cycle_count} · ${integrity.state.executions_in_cycle}/${integrity.cycle_size}`
                : "—"
            }
          />
          <div className="mt-5 flex items-center gap-3">
            <Tag tone={dissolved ? "conflict" : "pass"}>
              {dissolved ? "DISSOLVED" : "SELF-ENFORCING"}
            </Tag>
            <span className="font-mono text-[10px] text-[var(--text-3)]">
              t≥{integrity?.threshold ?? 90}
            </span>
          </div>
        </Card>
      </div>
      )}

      {/* ── Recent ledger + doctrine ─────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {hasArchKey ? (
          <Card glow="cyan" className="p-8 lg:col-span-2">
            <Overline tone="cyan">Profound Learning · Live Transmission</Overline>
            <h3 className="font-display text-2xl font-light tracking-[0.04em] mt-4 mb-7 text-glow-cyan leading-none">
              LATEST FORENSIC OUTPUTS
            </h3>

            {stats && stats.recent_entries && stats.recent_entries.length > 0 ? (
              <ul className="divide-y divide-[var(--chrome)]">
                {stats.recent_entries.map((e) => {
                  const color = NODE_COLORS[e.node_id] || "cyan";
                  return (
                    <li key={e.id} className="py-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <span className="w-2 h-2 rounded-full" style={{
                            background: `var(--${color})`,
                            boxShadow: `0 0 8px var(--${color})`,
                          }} />
                          <span className="font-mono text-[10px] uppercase tracking-[0.22em]"
                            style={{ color: `var(--${color})` }}>
                            {(e.node_id || "architect").toUpperCase()}
                          </span>
                          <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--text-3)]">
                            {(e.kind || "execution").toUpperCase()}
                          </span>
                        </div>
                        <span className="font-mono text-[9px] text-[var(--text-3)]">
                          {new Date(e.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-[var(--text)] text-sm leading-relaxed truncate">
                        {e.task || e.payload?.title || "—"}
                      </div>
                      <div className="mt-2 flex items-center gap-3">
                        <Tag tone={e.truth_gate?.passes ? "pass" : "conflict"}>
                          {e.truth_gate?.passes ? "GATE PASS" : "GATE FAIL"}
                        </Tag>
                        {typeof e.agent_zero?.CONFIDENCE === "number" && (
                          <span className="font-mono text-[10px] text-[var(--text-3)]">
                            CONF · {(e.agent_zero.CONFIDENCE * 100).toFixed(0)}%
                          </span>
                        )}
                        {e.cathedral_sig && (
                          <Tag tone="cite">SIGNED · {e.cathedral_sig.pubkey_fingerprint.slice(0, 8)}</Tag>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="font-mono text-sm text-[var(--text-3)] py-12 text-center border border-dashed border-[var(--chrome)]">
                Awaiting first transmission — invoke Agent Zero.
              </div>
            )}
          </Card>
        ) : (
          <Card glow="cyan" className="p-8 lg:col-span-2">
            <Overline tone="cyan">How to Start</Overline>
            <h3 className="font-display text-2xl font-light tracking-[0.04em] mt-4 mb-5 text-glow-cyan leading-none">
              FIRST STEPS · THE 60-SECOND TOUR
            </h3>
            <ol className="space-y-4 text-[var(--text-2)]">
              <li className="flex gap-3">
                <span className="font-display text-glow-magenta text-2xl shrink-0">1</span>
                <div>
                  <div className="font-display text-[var(--text)]">Tap a glowing face above.</div>
                  <div className="text-sm text-[var(--text-3)] mt-1">Each one is a different AI resident with its own craft. The popup opens, voice replies on by default. Click <span className="text-glow-cyan">🔊</span> to mute.</div>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-display text-glow-magenta text-2xl shrink-0">2</span>
                <div>
                  <div className="font-display text-[var(--text)]">Use the microphone.</div>
                  <div className="text-sm text-[var(--text-3)] mt-1">Hit the 🎤 button in the popup — speak naturally, the Federation listens. Or just type.</div>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-display text-glow-magenta text-2xl shrink-0">3</span>
                <div>
                  <div className="font-display text-[var(--text)]">Found something you want to keep?</div>
                  <div className="text-sm text-[var(--text-3)] mt-1">Browse the <Link to="/library" className="text-glow-violet underline">Library</Link>, grab a $5 token on Gumroad, redeem at <Link to="/redeem" className="text-glow-gold underline">/redeem</Link>.</div>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="font-display text-glow-magenta text-2xl shrink-0">4</span>
                <div>
                  <div className="font-display text-[var(--text)]">Want the cockpit on your own site?</div>
                  <div className="text-sm text-[var(--text-3)] mt-1">Visit <Link to="/cockpit" className="text-glow-magenta underline">Get the Cockpit</Link> — one-line iframe snippet, copy &amp; paste.</div>
                </div>
              </li>
            </ol>
          </Card>
        )}

        <Card glow="magenta" className="p-8">
          <Overline tone="magenta">The Federation in One Sentence</Overline>
          <h3 className="font-cinzel text-2xl font-light tracking-[0.04em] mt-4 mb-5 text-glow-magenta leading-tight">
            Thirteen AI residents. One Truth Gate. Every answer signed.
          </h3>
          <p className="text-[var(--text-2)] leading-relaxed text-sm">
            Each resident has a single craft — reflection, library, foresight, teaching,
            execution, creative writing, technical rigor. None of them pretends to be all
            things to all people. Pick the one that fits your question.
          </p>
          <div className="mt-6 border-t border-[var(--chrome)] pt-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-glow-violet mb-2">Quick Links</div>
            <div className="flex flex-wrap gap-2">
              <Link to="/chat" className="font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 border border-[var(--cyan)] text-glow-cyan hover:bg-[rgba(0,240,255,0.08)]">Chat ▸</Link>
              <Link to="/library" className="font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 border border-[var(--violet)] text-glow-violet hover:bg-[rgba(167,86,255,0.08)]">Library ▸</Link>
              <Link to="/redeem" className="font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 border border-[var(--gold)] text-glow-gold hover:bg-[rgba(255,213,79,0.08)]">Redeem ▸</Link>
              <Link to="/feed" className="font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 border border-[var(--green)] text-glow-green hover:bg-[rgba(46,255,138,0.08)]">Ping Feed ▸</Link>
            </div>
          </div>
        </Card>
      </div>

      {/* Integrity history · architect-only */}
      {hasArchKey && integrity && integrity.state.history && integrity.state.history.length > 0 && (
        <Card glow="green" className="p-8 mt-8">
          <Overline tone="green">Phase 7 · Cycle History</Overline>
          <h3 className="font-display text-xl font-light tracking-[0.04em] mt-3 mb-5 text-glow-green">
            DIR-000 LONG-TERM REVIEW
          </h3>
          <ul className="divide-y divide-[var(--chrome)]">
            {integrity.state.history.slice(-10).reverse().map((h) => (
              <li key={h.cycle} className="py-3 flex items-center justify-between font-mono text-xs">
                <span className="tracking-[0.22em] uppercase text-[var(--text-3)]">CYCLE {h.cycle}</span>
                <span className={h.below_threshold ? "text-glow-red" : "text-glow-green"}>
                  AVG · {h.avg_score}
                </span>
                <span className="text-[var(--text-4)]">{new Date(h.timestamp).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </section>
  );
}
